import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "../lib/cn";

type BentoGridProps = HTMLAttributes<HTMLDivElement>;

export const BentoGrid = forwardRef<HTMLDivElement, BentoGridProps>(
  ({ className, children, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        "grid w-full gap-4",
        "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6",
        "[grid-auto-rows:minmax(9rem,auto)]",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  ),
);
BentoGrid.displayName = "BentoGrid";

type BentoCellProps = HTMLAttributes<HTMLDivElement> & {
  span?: "sm" | "md" | "lg" | "xl";
  height?: "sm" | "md" | "lg";
  tone?: "default" | "primary" | "sakura" | "trust";
  title?: string;
  eyebrow?: string;
  icon?: ReactNode;
};

const spanMap: Record<NonNullable<BentoCellProps["span"]>, string> = {
  sm: "sm:col-span-1 lg:col-span-1 xl:col-span-2",
  md: "sm:col-span-2 lg:col-span-2 xl:col-span-3",
  lg: "sm:col-span-2 lg:col-span-3 xl:col-span-4",
  xl: "sm:col-span-2 lg:col-span-4 xl:col-span-6",
};

const heightMap: Record<NonNullable<BentoCellProps["height"]>, string> = {
  sm: "row-span-1",
  md: "row-span-2",
  lg: "row-span-3",
};

const toneMap: Record<NonNullable<BentoCellProps["tone"]>, string> = {
  default: "kj-glass",
  primary:
    "bg-gradient-to-br from-primary-500/20 via-sakura-500/10 to-transparent kj-glass",
  sakura: "bg-gradient-to-br from-sakura-300/30 to-transparent kj-glass",
  trust: "bg-gradient-to-br from-trust-500/20 to-transparent kj-glass",
};

export const BentoCell = forwardRef<HTMLDivElement, BentoCellProps>(
  (
    {
      className,
      children,
      span = "sm",
      height = "sm",
      tone = "default",
      title,
      eyebrow,
      icon,
      ...rest
    },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        "kj-bento-tile kj-noise relative overflow-hidden",
        spanMap[span],
        heightMap[height],
        toneMap[tone],
        className,
      )}
      {...rest}
    >
      {(eyebrow || icon) && (
        <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-wider text-fg-muted">
          {icon}
          {eyebrow && <span>{eyebrow}</span>}
        </div>
      )}
      {title && (
        <h3 className="mb-2 text-lg font-semibold text-fg">{title}</h3>
      )}
      {children}
    </div>
  ),
);
BentoCell.displayName = "BentoCell";
